import { FilterProps } from 'components/table/enhancements/filters/filterTypes'
import {
  getSortedUniqueValues,
  removeFilter,
  useClickOutside,
} from 'components/table/enhancements/filters/filterUtils'
import { FilterHeaderButton } from 'components/table/utility/FilterHeaderButton'
import { RemoveFilterButton } from 'components/table/utility/RemoveFilterButton'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  FilterContainer,
  LabelAndInputContainer,
  MultiSelectInput,
  RelativeWrap,
  StyledCloseIcon,
} from 'styles/assetDatabaseStyles'

const ALL_OPTION_VALUE = 'All'

/**
 * MultiSelectFilter - Allows users to filter table data by selecting multiple values
 *
 * @param showAllOption - When true, creates a permanent filter with an "All" option.
 *   "All" is selected by default (no filtering), selecting it clears other selections,
 *   and the filter cannot be removed (no X button). When false, filter is removable.
 */
export const MultiSelectFilter = <TData,>({
  column,
  columnHeader,
  setActiveFilters,
  inputVisible,
  navigate,
  disableUrlFilters,
  showAllOption = false,
}: FilterProps<TData>) => {
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const [isInputVisible, setInputVisible] = useState(inputVisible)
  const [currentFilterValues, setCurrentFilterValues] = useState<string[]>([])
  const columnFilterValue = column.getFilterValue() as string[]

  // Sync local state with column filter value from URL/external changes
  useEffect(() => {
    if (columnFilterValue && columnFilterValue !== currentFilterValues) {
      setCurrentFilterValues(columnFilterValue)
      setInputVisible(false)
    }
  }, [columnFilterValue, currentFilterValues])

  const sortedUniqueValues = useMemo(
    () => getSortedUniqueValues(column),
    [column]
  )

  // Updates filter state (column, local state, and URL)
  const updateFilterState = (newValues: string[]) => {
    const filterValue = newValues.length > 0 ? newValues : undefined
    column.setFilterValue(filterValue)
    setCurrentFilterValues(newValues)

    if (disableUrlFilters) return

    // For removable filters with no values, remove from active filters
    if (!filterValue && !showAllOption) {
      removeFilter(column, setActiveFilters, navigate)
      return
    }

    // Update URL with filter value (or undefined for "All")
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        [column.id]: filterValue,
      }),
    })
  }

  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target

    // Selecting "All" clears all other selections (resets to unfiltered state)
    if (showAllOption && value === ALL_OPTION_VALUE && checked) {
      updateFilterState([])
      return
    }

    // Toggle specific value selection
    const newValues = checked
      ? [...currentFilterValues, value]
      : currentFilterValues.filter((v) => v !== value)
    updateFilterState(newValues)
  }

  // If all values are removed, returns to "All" state for permanent filters
  const handleRemoveFilterValue = (value: string) => {
    const newValues = currentFilterValues.filter((v) => v !== value)
    updateFilterState(newValues)
  }

  const handleOutsideClick = () => {
    const hasNoValues = !currentFilterValues.length
    const shouldRemoveFilter = hasNoValues && !showAllOption

    if (shouldRemoveFilter) {
      removeFilter(column, setActiveFilters, navigate)
      return
    }
    // Close the dropdown
    setInputVisible(false)
  }

  useClickOutside(filterContainerRef, handleOutsideClick)

  const isAllSelected = showAllOption && currentFilterValues.length === 0

  const isOptionChecked = (value: string) => {
    return value === ALL_OPTION_VALUE
      ? isAllSelected
      : currentFilterValues.includes(value)
  }

  const renderSelectedValues = () => {
    if (isAllSelected) {
      return (
        <RemoveFilterButton aria-label={`Remove filter for ${columnHeader}`}>
          {ALL_OPTION_VALUE}
        </RemoveFilterButton>
      )
    }

    return currentFilterValues.map((value, index) => (
      <RemoveFilterButton
        key={`${column.id}-selected-${value}-${index}`}
        aria-label={`Remove ${value} filter for ${columnHeader}`}
        onClick={() => handleRemoveFilterValue(value)}>
        {value}
        <StyledCloseIcon />
      </RemoveFilterButton>
    ))
  }

  const renderOption = (value: string) => (
    <LabelAndInputContainer key={`${column.id}-option-${value}`}>
      <MultiSelectInput
        type='checkbox'
        value={value}
        checked={isOptionChecked(value)}
        onChange={handleSelectChange}
      />
      {value}
    </LabelAndInputContainer>
  )

  const allOptions = showAllOption
    ? [ALL_OPTION_VALUE, ...sortedUniqueValues]
    : sortedUniqueValues

  return (
    <RelativeWrap>
      <FilterHeaderButton
        aria-label={`Edit filter for ${columnHeader}`}
        tabIndex={0}
        onClick={() => setInputVisible((visible) => !visible)}>
        {columnHeader}
        {renderSelectedValues()}
        {!showAllOption && (
          <RemoveFilterButton
            aria-label={`Remove filter for ${columnHeader}`}
            onClick={() => removeFilter(column, setActiveFilters, navigate)}>
            <StyledCloseIcon />
          </RemoveFilterButton>
        )}
      </FilterHeaderButton>
      {isInputVisible && (
        <FilterContainer ref={filterContainerRef} data-input-visible='true'>
          {allOptions.map(renderOption)}
        </FilterContainer>
      )}
    </RelativeWrap>
  )
}
